import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Lock, MagnifyingGlass, Spinner, UsersThree, X } from "@phosphor-icons/react";
import { usersApi } from "@/lib/apiClient";

const RELATIONSHIP_LABELS = {
    following: "Following",
    requested: "Requested",
    incoming_request: "Requested you",
    self: "You",
};

function ResultBadge({ item }) {
    const relationship = RELATIONSHIP_LABELS[item.follow_state];
    if (!relationship && !item.mutual_friends_count && !item.is_private) return null;
    return (
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {relationship && (
                <span className="px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.18em]" style={{ background: "rgba(212,175,55,0.14)", color: "var(--gold)" }}>
                    {relationship}
                </span>
            )}
            {item.is_private && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.18em]" style={{ background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.72)" }}>
                    <Lock size={10} /> Private
                </span>
            )}
            {item.mutual_friends_count > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.18em]" style={{ background: "rgba(159,213,208,0.14)", color: "#9FD5D0" }}>
                    <UsersThree size={10} /> {item.mutual_friends_count} mutual
                </span>
            )}
        </div>
    );
}

export default function PassportUserSearch({ className = "", autoFocus = false, compact = false }) {
    const navigate = useNavigate();
    const rootRef = useRef(null);
    const cacheRef = useRef(new Map());
    const requestIdRef = useRef(0);
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [results, setResults] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");
    const [activeIndex, setActiveIndex] = useState(-1);

    const trimmedQuery = query.trim();
    const minimumChars = 2;

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(trimmedQuery), 320);
        return () => clearTimeout(timer);
    }, [trimmedQuery]);

    useEffect(() => {
        const handleClickAway = (event) => {
            if (!rootRef.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickAway);
        return () => document.removeEventListener("mousedown", handleClickAway);
    }, []);

    useEffect(() => {
        if (!debouncedQuery) {
            setResults([]);
            setNextCursor(null);
            setLoading(false);
            setError("");
            setActiveIndex(-1);
            return;
        }
        if (debouncedQuery.length < minimumChars) {
            setResults([]);
            setNextCursor(null);
            setLoading(false);
            setError("");
            setActiveIndex(-1);
            return;
        }
        const requestKey = debouncedQuery.toLowerCase();
        if (cacheRef.current.has(requestKey)) {
            const cached = cacheRef.current.get(requestKey);
            setResults(cached.items || []);
            setNextCursor(cached.next_cursor || null);
            setActiveIndex((cached.items || []).length ? 0 : -1);
            setError("");
            return;
        }
        const requestId = ++requestIdRef.current;
        setLoading(true);
        setError("");
        usersApi.search({ q: debouncedQuery, limit: 8 })
            .then((data) => {
                if (requestId !== requestIdRef.current) return;
                cacheRef.current.set(requestKey, data);
                setResults(data.items || []);
                setNextCursor(data.next_cursor || null);
                setActiveIndex((data.items || []).length ? 0 : -1);
            })
            .catch((err) => {
                if (requestId !== requestIdRef.current) return;
                setResults([]);
                setNextCursor(null);
                setActiveIndex(-1);
                setError(err?.response?.status === 429 ? "You’re searching quickly — pause for a moment." : err?.response?.data?.detail || "Search is unavailable right now.");
            })
            .finally(() => {
                if (requestId === requestIdRef.current) setLoading(false);
            });
    }, [debouncedQuery]);

    const hasQuery = debouncedQuery.length >= minimumChars;
    const showEmpty = hasQuery && !loading && !error && results.length === 0;
    const widthClass = compact ? "w-full max-w-[420px]" : "w-full max-w-[520px]";

    const placeholder = useMemo(
        () => (compact ? "Search travelers" : "Search travelers by name or @handle"),
        [compact],
    );

    const openProfile = (handle) => {
        setOpen(false);
        setQuery("");
        setDebouncedQuery("");
        setActiveIndex(-1);
        navigate(`/profile/${handle}`);
    };

    const clearSearch = () => {
        setQuery("");
        setDebouncedQuery("");
        setResults([]);
        setNextCursor(null);
        setError("");
        setActiveIndex(-1);
        setOpen(false);
    };

    const loadMore = async () => {
        if (!nextCursor || loadingMore || !debouncedQuery) return;
        const requestKey = debouncedQuery.toLowerCase();
        setLoadingMore(true);
        try {
            const data = await usersApi.search({ q: debouncedQuery, cursor: nextCursor, limit: 8 });
            setResults((prev) => {
                const merged = [...prev, ...(data.items || []).filter((item) => !prev.some((entry) => entry.handle === item.handle))];
                cacheRef.current.set(requestKey, { items: merged, next_cursor: data.next_cursor || null });
                return merged;
            });
            setNextCursor(data.next_cursor || null);
        } catch (err) {
            setError(err?.response?.status === 429 ? "You’re searching quickly — pause for a moment." : err?.response?.data?.detail || "Could not load more travelers.");
        } finally {
            setLoadingMore(false);
        }
    };

    const handleKeyDown = (event) => {
        if (!open && ["ArrowDown", "Enter"].includes(event.key)) setOpen(true);
        if (event.key === "Escape") {
            setOpen(false);
            setActiveIndex(-1);
            return;
        }
        if (!results.length) return;
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex((prev) => (prev + 1) % results.length);
        }
        if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((prev) => (prev <= 0 ? results.length - 1 : prev - 1));
        }
        if (event.key === "Enter") {
            event.preventDefault();
            const target = results[activeIndex] || results[0];
            if (target) openProfile(target.handle);
        }
    };

    return (
        <div ref={rootRef} className={`${widthClass} ${className}`} data-testid="passport-user-search">
            <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                    value={query}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={handleKeyDown}
                    autoFocus={autoFocus}
                    placeholder={placeholder}
                    className="w-full rounded-2xl pl-11 pr-12 py-3 bg-black/30 text-white border text-sm"
                    style={{ borderColor: "rgba(255,255,255,0.08)" }}
                    data-testid="passport-user-search-input"
                />
                {query && !loading && !loadingMore && (
                    <button type="button" onClick={clearSearch} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/45 hover:text-white transition-colors" data-testid="passport-user-search-clear-button" aria-label="Clear search">
                        <X size={14} />
                    </button>
                )}
                {(loading || loadingMore) && <Spinner size={14} className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-white/45" />}
            </div>

            {open && (
                <div className="absolute left-0 right-0 top-[calc(100%+12px)] z-[70] rounded-[28px] overflow-hidden border flex flex-col" style={{ maxHeight: "min(68vh, 480px)", background: "rgba(7,12,20,0.98)", borderColor: "rgba(212,175,55,0.18)", boxShadow: "0 24px 80px rgba(0,0,0,0.35)" }} data-testid="passport-user-search-results">
                    {debouncedQuery && debouncedQuery.length < minimumChars ? (
                        <div className="px-5 py-5 text-sm text-white/45" data-testid="passport-user-search-minimum-state">
                            Type at least {minimumChars} characters to search Passport travelers.
                        </div>
                    ) : !hasQuery ? (
                        <div className="px-5 py-5 text-sm text-white/45" data-testid="passport-user-search-idle">
                            Search by first name, last name, or @handle to jump into a traveler’s Passport profile.
                        </div>
                    ) : error ? (
                        <div className="px-5 py-5 text-sm text-[#F4A28F]" data-testid="passport-user-search-error">{error}</div>
                    ) : showEmpty ? (
                        <div className="px-5 py-5 text-sm text-white/45" data-testid="passport-user-search-empty">No travelers match “{debouncedQuery}”.</div>
                    ) : (
                        <>
                            <div className="overflow-y-auto overscroll-contain">
                                {results.map((item, index) => (
                                    <button
                                        key={item.handle}
                                        type="button"
                                        onClick={() => openProfile(item.handle)}
                                        className="w-full px-5 py-4 text-left border-b last:border-b-0 transition-colors"
                                        style={{ background: activeIndex === index ? "rgba(255,255,255,0.08)" : "transparent", borderColor: "rgba(255,255,255,0.05)" }}
                                        data-testid={`passport-user-search-result-${item.handle}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <img src={item.avatar_url || `https://api.dicebear.com/7.x/notionists/svg?seed=${item.handle}`} alt="" className="w-11 h-11 rounded-full object-cover" />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="text-sm text-white font-medium truncate">{item.name || item.handle}</span>
                                                    {item.is_verified && <CheckCircle size={12} weight="fill" color="#D4AF37" data-testid={`passport-user-search-verified-${item.handle}`} />}
                                                </div>
                                                <div className="text-xs text-white/50 truncate">@{item.handle}</div>
                                                <ResultBadge item={item} />
                                            </div>
                                            {item.tier && <div className="text-[10px] uppercase tracking-[0.18em] text-white/38">{item.tier}</div>}
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {nextCursor && (
                                <div className="p-3 border-t" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
                                    <button type="button" onClick={loadMore} disabled={loadingMore} className="w-full rounded-2xl px-4 py-3 text-sm text-white/75 border hover:bg-white/[0.04] transition-colors disabled:opacity-50" style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid="passport-user-search-load-more">
                                        {loadingMore ? "Loading more travelers…" : "Load more travelers"}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}